import { ConversionProductPage } from "@/components/conversion/ConversionProductPage";
import { TldrPanel, FactsPanel, ComparePanel } from "@/components/conversion/RestoredSections";
import { ProductNarrativeSections } from "@/components/truwe-visual/ProductNarrative";
import { OvaVitaminD3Evidence } from "@/components/pdp/OvaVitaminD3Evidence";
import { CompactPackSelector } from "@/components/conversion/CompactPackSelector";
import { CONVERSION_COPY } from "@/components/conversion/content";
import { createFileRoute, notFound, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Loader2,
  ShieldCheck,
  Leaf,
  FlaskConical,
  ArrowRight,
  Moon,
  Sparkles,
  Heart,
  Zap,
  Brain,
  Droplets,
  FlowerIcon,
  Activity,
  Sun,
  Wind,
  CircleDot,
  Scale,
  ShoppingCart,
  type LucideIcon,
} from "lucide-react";
import { BioAbsorbExplainer } from "@/components/pdp/BioAbsorbExplainer";
import { FrequentlyBoughtTogether, hasFbtOffer } from "@/components/pdp/FrequentlyBoughtTogether";
import { PreBookUpsellSheet } from "@/components/pdp/PreBookUpsellSheet";
import { PackSizeSelector } from "@/components/pdp/PackSizeSelector";
import InauguralProtocolCard from "@/components/pdp/InauguralProtocolCard";
import InauguralOfferBanner, { hasInauguralArt } from "@/components/pdp/InauguralOfferBanner";

import { useInauguralCampaign } from "@/lib/promo/useCampaigns";
// import { FoundingMemberForm } from "@/components/pdp/FoundingMemberForm";
import { RazorpayAffordabilityWidget } from "@/components/RazorpayAffordabilityWidget";
import { buildFaqPageSchema } from "@/lib/faqSchema";
import { NotifyMeDialog } from "@/components/NotifyMeDialog";
import { PreReserveDialog } from "@/components/pdp/PreReserveDialog";
import { fetchProductByHandle } from "@/lib/shopify";
import { getFallbackProduct } from "@/lib/fallbackProducts";
import { loadBundlePage, type BundlePageData } from "@/lib/bundlePage";
import { BundleOfferPage } from "@/components/bundle/BundleOfferPage";
import { bundleGalleryFor } from "@/lib/bundleGallery";
import { bundleBySlug, canonicalSlugForLegacy } from "@/lib/ovaEcosystem";
import { getOvaBundleKind, ovaBundleContent } from "@/components/bundle/ovaBundleContent";
import { buildBundleProductSchema, buildBundleBreadcrumbSchema } from "@/lib/seo/bundle-schema";
import { OvaProtocolLinks } from "@/components/pdp/OvaProtocolLinks";

type ProductReviewLite = {
  rating: number;
  title: string | null;
  comment: string | null;
  reviewer_name: string | null;
  created_at: string;
};

import {
  SOLD_OUT,
  isComingSoon,
  PREORDER_ACTIVE,
  PREORDER_NOTE,
  PREORDER_NOTE_SHORT,
  PREORDER_DISPATCH_LABEL,
  PREORDER_DISPATCH_ISO,
} from "@/lib/storeStatus";
import { buildProductSchema } from "@/lib/seo/product-schema";
import { Button } from "@/components/ui/button";
import { ExpandableHtml } from "@/components/ExpandableHtml";
import { useT } from "@/lib/i18n/provider";
import { useProductDescription, useProductTagline, useProductRoutine } from "@/lib/i18n/content";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useCartStore } from "@/stores/cartStore";
import { toast } from "sonner";
import { getFormulaKeyForHandle } from "@/lib/ingredientBenefits";
import { StudiedEffects } from "@/components/StudiedEffects";
import { ProtocolBundle } from "@/components/ProtocolBundle";
import { AbsorptionRateChart, PRODUCT_ABSORPTION_ROWS } from "@/components/AbsorptionRateChart";
import { getPDPContentForHandle } from "@/lib/productContent";
import {
  mergePdpForHandle,
  mergeGallery,
  packPricingFromOverride,
  type ProductPageOverride,
} from "@/lib/productPageContent";
import { getPublishedProductPage } from "@/lib/product-page.functions";
import { isAllowedOrigin, parentTargetOrigin } from "@/lib/cms/studio-bridge";

import { getProductReviews } from "@/lib/reviews.functions";
import { setRuntimePackPricing, getPackPricingTable } from "@/lib/checkout-pricing";
import {
  getEffectivePack,
  getPackOptionViews,
  formatInr,
  prepaidEstimate,
} from "@/lib/promo/effectivePacks";
import { TLDRCard } from "@/components/pdp/TLDRCard";
import { OvaCommunityIllustration } from "@/components/pdp/OvaCommunityIllustration";

import { QuickFactsTable } from "@/components/pdp/QuickFactsTable";
import { ComparisonTable } from "@/components/pdp/ComparisonTable";

import { PDPFaq } from "@/components/pdp/PDPFaq";
import { GutLiverAxisSection } from "@/components/pdp/GutLiverAxisSection";
import { LiverResearchCards } from "@/components/pdp/LiverResearchCards";
import { PincodeDeliveryBar } from "@/components/pdp/PincodeDeliveryBar";
import { PaymentTrustBadges } from "@/components/PaymentTrustBadges";
import { Reveal } from "@/components/Reveal";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { RelatedReading } from "@/components/RelatedReading";
import { articlesForProduct } from "@/lib/clusters";
import { COLLECTIONS, COLLECTIONS_FOR_PRODUCT } from "@/lib/collections";
import { Link as TSLink } from "@tanstack/react-router";

import { ReviewsSection } from "@/components/reviews/ReviewsSection";
import { UrgencyInline } from "@/components/urgency/UrgencyInline";
import { UrgencyLabel } from "@/components/urgency/UrgencyLabel";

import { productIdForHandle } from "@/lib/productIds";



type QuickBenefit = { icon: LucideIcon; label: string };

const QUICK_BENEFITS: Record<"liver" | "mag" | "ova", QuickBenefit[]> = {
  liver: [
    { icon: Sparkles, label: "Lighter\nMornings" },
    { icon: Activity, label: "Smoother\nDigestion" },
    { icon: Zap, label: "Steady\nEnergy" },
    { icon: FlowerIcon, label: "Clearer\nSkin" },
    { icon: ShieldCheck, label: "Stronger\nImmunity" },
    { icon: Leaf, label: "Research\nBacked Detox" },
  ],
  mag: [
    { icon: Moon, label: "Deeper\nRestful Sleep" },
    { icon: Brain, label: "Calmer\nMind" },
    { icon: Heart, label: "Heart &\nNerve Health" },
    { icon: Activity, label: "Less Muscle\nCramps" },
    { icon: Zap, label: "Better\nRecovery" },
    { icon: Droplets, label: "5 Forms of\nMagnesium" },
  ],
  ova: [
    { icon: CircleDot, label: "Regular\nCycles" },
    { icon: Sun, label: "Steadier\nMood" },
    { icon: FlowerIcon, label: "Clearer\nSkin" },
    { icon: Scale, label: "Easier\nWeight Management" },
    { icon: Wind, label: "Fewer Sugar\nCravings" },
    { icon: Sparkles, label: "Gold-Standard\n40:1 Ratio" },
  ],
};

function getQuickBenefitsForHandle(handle: string): QuickBenefit[] | null {
  const h = handle.toLowerCase();
  if (h.includes("liver") || h.includes("biome")) return QUICK_BENEFITS.liver;
  if (h.includes("mag") || h.includes("absorb")) return QUICK_BENEFITS.mag;
  if (h.includes("ova") || h.includes("inositol")) return QUICK_BENEFITS.ova;
  return null;
}
import liverBiome01 from "@/assets/liver-biome-v2/01-hero.webp.asset.json";
import magAbsorbHeroAsset from "@/assets/product-hero/mag-absorb-hero.png.asset.json";
import ovaBalanceHeroAsset from "@/assets/product-hero/ova-balance-hero.png.asset.json";
import { GalleryActions } from "@/components/pdp/GalleryActions";
import { OvaScrollGallery } from "@/components/pdp/OvaScrollGallery";
import { LiverScrollGallery } from "@/components/pdp/LiverScrollGallery";
import { MagScrollGallery } from "@/components/pdp/MagScrollGallery";
import {
  LIVER_BIOME_GALLERY,
  MAG_ABSORB_GALLERY,
  OVA_BALANCE_GALLERY,
} from "@/lib/productGallery";


type ProductTheme = { brand: string; brandDeep: string; brandSoft: string };

const PRODUCT_THEMES: Record<string, ProductTheme> = {
  "liver-biome": {
    brand: "oklch(0.55 0.09 105)", // olive accent
    brandDeep: "oklch(0.32 0.05 105)",
    brandSoft: "oklch(0.96 0.025 105)",
  },
  default: {
    brand: "oklch(0.55 0.21 263)",
    brandDeep: "oklch(0.296 0 0)",
    brandSoft: "oklch(0.97 0.015 250)",
  },
};

function getProductTheme(handle: string, _productType?: string): ProductTheme {
  // Liver Biome+ gets its own olive accent; everything else stays brand blue.
  if (handle.startsWith("liver-biome")) return PRODUCT_THEMES["liver-biome"];
  return PRODUCT_THEMES.default;
}

// AggregateRating is now sourced from real approved reviews in the DB
// (loader fetches via getProductReviews). Google's Rich Results validator
// flags fabricated/placeholder ratings, so we only emit when count > 0.

export const Route = createFileRoute("/products/$handle")({
  /** `?pack=1|2|3` deep-links a pack size (used by the Merchant/Meta feeds). */
  validateSearch: (
    search: Record<string, unknown>,
  ): { pack?: 1 | 2 | 3; editorPreview?: 1; classic?: 1 } => {
    const raw = Number(search["pack"]);
    const out: { pack?: 1 | 2 | 3; editorPreview?: 1; classic?: 1 } = {};
    if (raw === 1 || raw === 2 || raw === 3) out.pack = raw;
    // Same-origin admin editor live preview (draft content via postMessage).
    if (String(search["editorPreview"] ?? "") === "1") out.editorPreview = 1;
    if (String(search["classic"] ?? "") === "1") out.classic = 1;
    return out;
  },
  loader: async ({ params }) => {
    // Renamed bundle URLs keep working: one permanent hop to the canonical slug.
    const renamed = canonicalSlugForLegacy(params.handle);
    if (renamed) {
      throw redirect({
        to: "/products/$handle",
        params: { handle: renamed },
        statusCode: 301,
      });
    }
    let product: Awaited<ReturnType<typeof fetchProductByHandle>> | null = null;
    let isFallback = false;
    try {
      product = await fetchProductByHandle(params.handle);
    } catch (err) {
      console.warn("Shopify product fetch failed, trying fallback:", err);
    }
    if (!product) {
      const fallback = getFallbackProduct(params.handle);
      if (!fallback) {
        // Not a SKU — the handle may be a live bundle rule slug.
        const bundle = await loadBundlePage(params.handle);
        if (bundle) {
          return {
            product: null,
            isFallback: false,
            reviewStats: null as { avg: number; count: number } | null,
            topReviews: [] as ProductReviewLite[],
            pageOverride: null as ProductPageOverride | null,
            bundle,
          };
        }
        throw notFound();
      }
      product = fallback;
      isFallback = true;
    }

    // Pull real review stats + top approved reviews for JSON-LD rich results.
    let reviewStats: { avg: number; count: number } | null = null;
    let topReviews: ProductReviewLite[] = [];
    const productId = productIdForHandle(params.handle);
    if (productId) {
      try {
        const res = await getProductReviews({ data: { productId, sort: "highest" } });
        // All approved reviews (verified purchases + disclosed beta testers)
        // count towards the public rating, so crawlers see the same numbers
        // shoppers see on the page.
        if (res.stats.count > 0) {
          reviewStats = { avg: res.stats.avg, count: res.stats.count };
          topReviews = (res.reviews as ProductReviewLite[]).slice(0, 5);
        }

      } catch (err) {
        console.warn("Review stats fetch failed for", params.handle, err);
      }
    }

    let pageOverride: ProductPageOverride | null = null;
    try {
      pageOverride = await getPublishedProductPage({ data: { handle: params.handle } });
    } catch (err) {
      console.warn("Product page override fetch failed", err);
    }

    return {
      product,
      isFallback,
      reviewStats,
      topReviews,
      pageOverride,
      bundle: null as BundlePageData | null,
    };

  },

  head: ({ params, loaderData }) => {
    const product = loaderData?.product;
    const desc = product?.description?.slice(0, 160) ?? "TruWe nutraceutical product.";
    const image = product?.images.edges[0]?.node.url;
    const price = product?.priceRange.minVariantPrice.amount;
    const currency = product?.priceRange.minVariantPrice.currencyCode ?? "INR";
    const url = `https://truwe.in/products/${params.handle}`;
    const override = loaderData?.pageOverride ?? null;
    const pdp = mergePdpForHandle(params.handle, override);
    const seoDescByHandle: Record<string, string> = {
      "mag-absorb-pro":
        "Buy the best magnesium supplement in India. TruWe Mag Absorb Pro with 5 magnesium forms including liposomal bisglycinate supports sleep, muscle recovery, and stress relief.",
      "liver-biome-plus":
        "Buy the best liver detox supplement in India. TruWe Liver Biome Plus with liposomal silymarin, NAC & 10B probiotics supports liver health and digestion.",
      "ova-balance":
        "Ova Balance combines 2000 mg Myo + 50 mg D-Chiro Inositol (40:1) with berberine, active folate and minerals for daily cycle and metabolic support.",
    };
    const seoTitleByHandle: Record<string, string> = {
      "mag-absorb-pro": "Buy Best Magnesium Supplements online in India",
      "ova-balance": "PCOS Supplement with 40:1 Inositol | Ova Balance | TruWe",
      "liver-biome-plus": "Buy the Best Liver Detox Supplement in India",
    };

    const bundle = loaderData?.bundle;
    const bundleEntity = bundle ? bundleBySlug(bundle.rule.slug) : undefined;
    const seoTitle = bundleEntity
      ? bundleEntity.seoTitle
      : bundle
      ? `${bundle.rule.title} — Save ${bundle.percent}% | TruWe`
      : ((override?.seo?.title || "").trim() ||
        seoTitleByHandle[params.handle] ||
        `${product?.title ?? "Product"} | TruWe`);
    const seoDesc = bundleEntity
      ? bundleEntity.seoDescription
      : bundle
      ? `${bundle.rule.tagline || bundle.rule.title} Buy the ${bundle.rule.title} for ₹${bundle.total.toLocaleString("en-IN")} and save ₹${bundle.savings.toLocaleString("en-IN")}.`.slice(0, 158)
      : ((override?.seo?.description || "").trim() ||
        seoDescByHandle[params.handle] ||
        (pdp ? pdp.tldr.what.slice(0, 158) : desc));

    // Bundle share/schema image: the photograph the bundle page actually shows.
    const bundleKind = bundle ? getOvaBundleKind(bundle.rule.matchHandles) : null;
    const bundleImage =
      bundleKind ? `https://truwe.in${ovaBundleContent[bundleKind].heroImage.src}` : null;
    const bundleAvailability: "InStock" | "OutOfStock" | "PreOrder" =
      SOLD_OUT || (bundle?.items ?? []).some((p) => isComingSoon(p.handle))
        ? "OutOfStock"
        : PREORDER_ACTIVE
          ? "PreOrder"
          : "InStock";

    return {

      meta: [
        { title: seoTitle },
        { name: "description", content: seoDesc },
        { property: "og:title", content: seoTitle },
        { property: "og:description", content: seoDesc },
        { property: "og:type", content: "product" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:url", content: url },
        ...(() => {
          const bundleShot = bundle ? bundleGalleryFor(bundle.rule.slug)[0]?.url : undefined;
          const heroShareByHandle: Record<string, string> = {
            "ova-balance": `https://truwe.in${ovaBalanceHeroAsset.url}`,
          };
          const ogImage =
            bundleImage ??
            (bundleShot ? `https://truwe.in${bundleShot}` : undefined) ??
            (bundle ? undefined : heroShareByHandle[params.handle]) ??
            image ??
            bundle?.items[0]?.images.edges[0]?.node.url;
          return ogImage
            ? [{ property: "og:image", content: ogImage }, { name: "twitter:image", content: ogImage }]
            : [];
        })(),


        ...(bundle
          ? [
              { property: "product:price:amount", content: String(Math.round(bundle.total)) },
              { property: "product:price:currency", content: "INR" },
            ]
          : price
          ? [
              { property: "product:price:amount", content: price },
              { property: "product:price:currency", content: currency },
            ]
          : []),



      ],
      links: [
        { rel: "canonical", href: url },
        ...(() => {
          const heroByHandle: Record<string, string> = {
            "mag-absorb-pro": magAbsorbHeroAsset.url,
            "liver-biome-plus": liverBiome01.url,
            "ova-balance": ovaBalanceHeroAsset.url,
          };
          const preload = heroByHandle[params.handle];
          return preload
            ? [{ rel: "preload", as: "image", href: preload, fetchPriority: "high" } as unknown as { rel: string; href: string }]
            : [];
        })(),
      ],
      scripts: bundleEntity && bundle
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(
                buildBundleProductSchema({
                  bundle: bundleEntity,
                  price: bundle.total,
                  availability: bundleAvailability,
                  availabilityStarts: PREORDER_ACTIVE ? PREORDER_DISPATCH_ISO : null,
                  image: bundleImage,
                }),
              ),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(buildBundleBreadcrumbSchema(bundleEntity)),
            },
          ]
        : product

        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(
                buildProductSchema({
                  handle: params.handle,
                  name: product.title,
                  alternateName: seoTitle,
                  description: seoDesc,
                  images: [image ?? "https://truwe.in/og-image.jpg"],
                  // Default (30-day) pack only — no AggregateOffer for variants.
                  // Live admin-published price wins; never let this go null.
                  price: getPackPricingTable(params.handle)?.[1]?.price ?? price,
                  currency,
                  inStock: !SOLD_OUT && !isComingSoon(product.handle),
                  // The pack is paid for now and dispatched on the campaign
                  // date, so the honest schema state is PreOrder, not InStock.
                  availability: SOLD_OUT
                    ? "OutOfStock"
                    : isComingSoon(product.handle) || PREORDER_ACTIVE
                      ? "PreOrder"
                      : "InStock",
                  availabilityStarts: PREORDER_ACTIVE ? PREORDER_DISPATCH_ISO : null,

                  sku: params.handle,
                  aggregateRating:
                    loaderData?.reviewStats && loaderData.reviewStats.count > 0
                      ? {
                          ratingValue: loaderData.reviewStats.avg,
                          reviewCount: loaderData.reviewStats.count,
                        }
                      : null,
                  reviews: loaderData?.topReviews ?? [],
                }),
              ),
            },


            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: "https://truwe.in/" },
                  { "@type": "ListItem", position: 2, name: "Shop", item: "https://truwe.in/shop" },
                  { "@type": "ListItem", position: 3, name: product.title, item: url },
                ],
              }),
            },
            ...(pdp
              ? [
                  {
                    type: "application/ld+json",
                    children: JSON.stringify(
                      buildFaqPageSchema({
                        pageUrl: url,
                        faqs: pdp.faqs,
                      }),
                    ),
                  },
                  {
                    type: "application/ld+json",
                    children: JSON.stringify({
                      "@context": "https://schema.org",
                      "@type": "MedicalWebPage",
                      name: `${product.title} ${pdp.h1Suffix}`,
                      url,
                      description: pdp.tldr.what,
                      audience: { "@type": "MedicalAudience", audienceType: "Patient" },
                      // No lastReviewed / reviewedBy: we do not claim an
                      // independent, named clinical review on this page.

                      // Reference the single Product node above by @id — do NOT
                      // inline a DietarySupplement here. DietarySupplement is a
                      // Product subtype, so an inline node renders as a second
                      // Product block without offers and fails Rich Results.
                      about: { "@id": `${url}#product` },
                      mainEntity: { "@id": `${url}#product` },
                    }),
                  },
                ]
              : []),
          ]
        : [],
    };
  },
  component: ProductRoutePage,
  errorComponent: ({ error }) => {
    console.error("Product route error:", error);
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl">Couldn't load product</h1>
        <p className="mt-2 text-sm text-muted-foreground">Please try again in a moment.</p>
      </div>
    );
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-4 py-20 text-center">
      <h1 className="font-display text-2xl">Product not found</h1>
    </div>
  ),
});

function ProductRoutePage() {
  const { bundle } = Route.useLoaderData();
  if (bundle) return <BundleOfferPage data={bundle} />;
  return <ProductPage />;
}

function ProductPage() {
  const tt = useT();
  const localizedDescription = useProductDescription();
  const localizedTagline = useProductTagline();
  const localizedRoutine = useProductRoutine();
  const loaded = Route.useLoaderData();
  const { isFallback, reviewStats, topReviews } = loaded;
  const product = loaded.product!;
  const { pack, editorPreview, classic } = Route.useSearch();
  // Live draft preview: the admin editor posts the in-progress override
  // document into this iframe. Same-origin only, never on the public page.
  const [previewOverride, setPreviewOverride] = useState<ProductPageOverride | null>(null);
  useEffect(() => {
    if (!editorPreview || typeof window === "undefined") return;
    const onMsg = (e: MessageEvent) => {
      if (!isAllowedOrigin(e.origin)) return;
      const d = e.data as { type?: string; override?: ProductPageOverride } | null;
      if (!d || d.type !== "truwe:product-page-preview") return;
      setPreviewOverride(d.override ?? {});
    };
    window.addEventListener("message", onMsg);
    window.parent?.postMessage({ type: "truwe:product-page-preview-ready" }, parentTargetOrigin());
    return () => window.removeEventListener("message", onMsg);
  }, [editorPreview]);

  const pageOverride = (editorPreview ? previewOverride : null) ?? loaded.pageOverride ?? null;

  // Keep cart + checkout discount maths on the published pack prices.
  const packOverrideTable = packPricingFromOverride(product.handle, pageOverride);
  setRuntimePackPricing(product.handle, packOverrideTable);

  const navigate = useNavigate();
  const variant = product.variants.edges[0]?.node;
  const price = parseFloat(product.priceRange.minVariantPrice.amount);
  const compare = product.compareAtPriceRange?.minVariantPrice?.amount
    ? parseFloat(product.compareAtPriceRange.minVariantPrice.amount)
    : null;
  const [qty, setQty] = useState<number>(pack ?? 2);
  const inauguralCampaign = useInauguralCampaign(product?.handle);
  // Offers never show on sold-out / coming-soon products.
  const activeCampaign =
    inauguralCampaign && !SOLD_OUT && !isComingSoon(product.handle)
      ? inauguralCampaign
      : null;
  const [prepaid, setPrepaid] = useState(true);
  const addItems = useCartStore((s) => s.addItems);
  const isLoading = useCartStore((s) => s.isLoading);

  const [justAdded, setJustAdded] = useState(false);

  // Identity: track product_view for retargeting + welcome-back personalization.
  useEffect(() => {
    if (typeof window === "undefined") return;
    void import("@/lib/identity/tracker").then(({ track }) =>
      track({
        eventType: "product_view",
        properties: { handle: product.handle, title: product.title, price },
      }),
    );
    // Repeat-interest: attribute this page's engagement summary (active
    // time + depth, emitted on leave) to this product.
    void import("@/lib/customer-intelligence/tracker").then((m) =>
      m.setProductPageHandle(product.handle),
    );
    // Device-level interest: remembers which product this browser leans to,
    // so it is already attached once the visitor identifies themselves.
    void (async () => {
      try {
        const { getDeviceIdWhenIdle } = await import("@/lib/identity/deviceFingerprint");
        const deviceId = await getDeviceIdWhenIdle();
        if (!deviceId) return;
        const { recordDeviceIntent } = await import("@/lib/device-identity.functions");
        await recordDeviceIntent({
          data: { deviceId, signal: "product_view", handle: product.handle },
        });
      } catch {
        /* interest tracking is best-effort */
      }
    })();
    // Shared ecommerce layer — pushes GA4 `view_item` to dataLayer AND fires
    // Meta ViewContent. Consent / internal-traffic checks live in wrappers.
    void import("@/lib/analytics/ecommerce").then(({ trackViewItem }) =>
      trackViewItem({
        id: product.handle,
        name: product.title,
        price,
        quantity: 1,
      }),
    );
  }, [product.handle, product.title, price]);

  // Repeat-interest: report which meaningful sections were actually seen.
  // Hero counts on mount; deeper sections count once ≥25% visible. Ranked
  // section ids live in SECTION_RANK (src/lib/repeat-interest.ts).
  useEffect(() => {
    if (typeof window === "undefined") return;
    let disposed = false;
    let observer: IntersectionObserver | null = null;
    void import("@/lib/customer-intelligence/tracker").then((m) => {
      if (disposed) return;
      m.markSection("hero");
      observer = new IntersectionObserver(
        (entries) => {
          for (const en of entries) {
            if (!en.isIntersecting) continue;
            const id = (en.target as HTMLElement).dataset.engagementSection;
            if (id) {
              m.markSection(id);
              // Denominator for the admin click-interest map: how many
              // people actually saw this section.
              m.track("section_view", {
                sectionId: id,
                handle: product.handle,
                pageType: "product",
                productId: product.handle,
                deviceType:
                  window.innerWidth < 640
                    ? "mobile"
                    : window.innerWidth < 1024
                      ? "tablet"
                      : "desktop",
              });
              observer?.unobserve(en.target);
            }
          }
        },
        { threshold: 0.25 },
      );
      document
        .querySelectorAll("[data-engagement-section]")
        .forEach((el) => observer!.observe(el));
    });
    return () => {
      disposed = true;
      observer?.disconnect();
    };
  }, [product.handle]);

  // Click interest map (Admin → Intelligence). Element-level clicks only:
  // no coordinates, no recordings, no input values.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let dispose: (() => void) | null = null;
    let disposed = false;
    void import("@/lib/customer-intelligence/click-tracker").then((m) => {
      if (disposed) return;
      dispose = m.mountClickTracking({ handle: product.handle, pageType: "product" });
    });
    return () => {
      disposed = true;
      dispose?.();
    };
  }, [product.handle]);

  const [upsellOpen, setUpsellOpen] = useState(false);

  async function handleAdd(buyNow = false) {
    if (!variant) return;
    if (isFallback) {
      toast.error("Live store is temporarily unavailable. Please try again shortly.");
      return;
    }
    if (buyNow) {
      // "Buy now" now goes through the cart so Cash on Delivery, coupons,
      // bundle pricing and TruCoins are always available. Checkout intent is
      // still recorded here.
      try {
        const { trackBeginCheckout } = await import("@/lib/analytics/ecommerce");
        const { track: ciTrack } = await import("@/lib/customer-intelligence/tracker");
        ciTrack("begin_checkout", { source: "buy_now", handle: product.handle, qty });
        trackBeginCheckout({
          attemptKey: `buynow:${variant.id}:${qty}:${Date.now() >> 10}`,
          items: [
            {
              id: product.handle,
              name: product.title,
              price: Number(variant.price?.amount ?? price),
              quantity: qty,
            },
          ],
        });
      } catch {
        /* analytics never blocks checkout */
      }
    }


    const cartUrl = await addItems([
      {
        variantId: variant.id,
        productHandle: product.handle,
        productTitle: product.title,
        imageUrl: product.images.edges[0]?.node.url ?? null,
        price: variant.price,
        quantity: qty,
      },
    ]);
    if (!cartUrl) {
      toast.error("Couldn't add to cart. Please try again.");
      return;
    }
    toast.success(`${product.title} added to cart`);
    // Identity: dispatch action — picks up any popup with trigger { kind: "action", action: "add_to_cart" }
    // and also writes an `add_to_cart` event to the lead profile.
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("identity:action", {
          detail: { action: "add_to_cart", properties: { handle: product.handle, qty } },
        }),
      );
    }
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1400);
    (window as unknown as { __openCart?: () => void }).__openCart?.();
  }


  const handleLower = product.handle.toLowerCase();
  const titleLower = product.title.toLowerCase();
  const isLiverBiome = handleLower.includes("liver-biome");
  const isMagAbsorb = handleLower.includes("mag-absorb") || titleLower.includes("mag absorb");
  const isOvaBalance = handleLower.includes("ova") || titleLower.includes("ova balance");
  const galleryImages = isLiverBiome
    ? LIVER_BIOME_GALLERY
    : isMagAbsorb
      ? MAG_ABSORB_GALLERY
      : isOvaBalance
        ? OVA_BALANCE_GALLERY
        : product.images.edges.map((e: { node: { url: string; altText: string | null } }) => ({ src: e.node.url, alt: e.node.altText ?? product.title }));
  const galleryImagesResolved = mergeGallery(galleryImages, pageOverride);
  const [activeImg, setActiveImg] = useState(0);

  const theme = getProductTheme(product.handle, product.productType);

  // Mobile-first conversion layout for the three adult SKUs. `?classic=1` restores the previous page.
  if (classic !== 1 && !editorPreview && CONVERSION_COPY[product.handle]) {
    const pdp = mergePdpForHandle(product.handle, pageOverride);
    const comingSoon = SOLD_OUT || isComingSoon(product.handle);
    const effective = getEffectivePack(product.handle, qty, activeCampaign, pageOverride);
    const options = getPackOptionViews(product.handle, pageOverride, activeCampaign) ?? [];
    const unavailable = isFallback || !variant?.availableForSale || isLoading;
    const label = comingSoon ? (product.handle === "ova-balance" ? "Pre-booking open" : "Coming soon") : effective?.supply ?? `${qty} bottle${qty === 1 ? "" : "s"}`;
    const narrativeKey = getFormulaKeyForHandle(product.handle);
    const doPurchase = () => {
      if (PREORDER_ACTIVE && hasFbtOffer(product.handle) && variant?.id) { setUpsellOpen(true); return; }
      void handleAdd(true);
    };
    const purchaseAction = (source: string) => comingSoon ? (
      <PreReserveDialog productHandle={product.handle} productName={product.title} source={source}
        trigger={<button type="button" className="twc-primary">{product.handle === "ova-balance" ? "Pre-book now" : "Pre-reserve"}</button>} />
    ) : (
      <button type="button" className="twc-primary" disabled={unavailable} onClick={doPurchase} data-ci-click={source}>
        {isLoading ? "Please wait…" : PREORDER_ACTIVE ? "Pre-book now" : "Buy now"}
      </button>
    );
    return <>
      <ConversionProductPage
        handle={product.handle}
        hideSticky={upsellOpen}
        priceLabel={!comingSoon && !isFallback && effective ? formatInr(effective.price) : undefined}
        supplyLabel={label}
        stickyAction={purchaseAction("pdp-sticky-add-to-cart")}
        narrative={narrativeKey ? <ProductNarrativeSections productKey={narrativeKey} /> : null}
        gallery={product.handle === "liver-biome-plus" ? (
          <LiverScrollGallery
            images={galleryImagesResolved}
            galleryActions={<GalleryActions handle={product.handle} title={product.title} />}
          />
        ) : product.handle === "mag-absorb-pro" ? (
          <MagScrollGallery
            images={galleryImagesResolved}
            galleryActions={<GalleryActions handle={product.handle} title={product.title} />}
          />
        ) : isOvaBalance ? (
          <OvaScrollGallery
            images={galleryImagesResolved}
            galleryActions={<GalleryActions handle={product.handle} title={product.title} />}
          />
        ) : <>
            <div className="twc-selected-image" style={{ position: "relative" }}>
              <SwipeableImage
                images={galleryImagesResolved}
                active={activeImg}
                onPrev={() => setActiveImg(i => (i - 1 + galleryImagesResolved.length) % galleryImagesResolved.length)}
                onNext={() => setActiveImg(i => (i + 1) % galleryImagesResolved.length)}
              />
              <GalleryActions handle={product.handle} title={product.title} />
            </div>
            <div className="twc-thumbnails" aria-label="Product images">{galleryImagesResolved.map((image, i) => <button key={image.src + i} type="button" aria-label={`Show image ${i + 1}`} aria-pressed={i === activeImg} onClick={() => setActiveImg(i)}><img src={image.src} alt="" width={56} height={56} loading="lazy" decoding="async" /></button>)}</div>
          </>}
        purchase={<>
          {!comingSoon && <CompactPackSelector value={qty} onChange={setQty} options={options.map(o => ({ qty:o.qty, supply:o.supply, unitsLabel:o.unitsLabel, priceLabel:formatInr(o.price), price:o.price, compareAt:o.compareAt > o.price ? o.compareAt : undefined, note:o.isCampaign ? o.subLabel : undefined, disabled:isFallback || isLoading }))} />}
          {reviewStats && reviewStats.count > 0 && <a className="twc-text-link twc-rating-link" href="#twc-evidence" aria-label={`${reviewStats.avg.toFixed(1)} out of 5 from ${reviewStats.count} reviews`}>
            <span className="twc-stars" aria-hidden="true">{[0,1,2,3,4].map(i => <svg key={i} viewBox="0 0 20 20" width={14} height={14} fill={i < Math.round(reviewStats.avg) ? "currentColor" : "none"} stroke="currentColor" strokeWidth={1.5}><path d="M10 1.6l2.5 5.2 5.7.8-4.1 4 1 5.7L10 14.6l-5.1 2.7 1-5.7-4.1-4 5.7-.8z" strokeLinejoin="round" /></svg>)}</span>
            {reviewStats.avg.toFixed(1)} / 5 · {reviewStats.count} reviews
          </a>}
          {isFallback && <p role="status">Live pricing and checkout are temporarily unavailable.</p>}
          {!comingSoon && !isFallback && effective && <RazorpayAffordabilityWidget amountPaise={Math.round(effective.price * 100)} />}
          <div className="twc-action-stack">
            {product.handle === "ova-balance" && (
              <p className="flex items-center gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3.5 py-2.5 text-[12px] font-semibold leading-snug text-amber-700" data-ci="ova-scarcity">
                <span aria-hidden className="relative flex h-2 w-2 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-pulse rounded-full bg-amber-500 opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
                </span>
                <span>
                  Limited quantities — booking fast
                  <span className="block text-[11px] font-medium normal-case tracking-normal text-amber-700/90">
                    Only a small batch is available in this pre-book window. Reserve yours early.
                  </span>
                </span>
              </p>
            )}
            {purchaseAction("pdp-buy-now")}
            {comingSoon ? <NotifyMeDialog productName={product.title} source="conversion-preview" trigger={<button type="button" className="twc-text-link">Notify me</button>} /> : <button type="button" className="twc-secondary" disabled={unavailable} onClick={() => void handleAdd(false)} data-ci-click="pdp-add-to-cart">{justAdded ? "Added to cart" : "Add to cart"}</button>}
          </div>
          {!comingSoon && PREORDER_ACTIVE && <p className="twc-purchase-note">{PREORDER_NOTE}</p>}
          {!comingSoon && <div className="twc-buy-extras"><PincodeDeliveryBar /></div>}
        </>}
        tldr={<TldrPanel tldr={pdp?.tldr} />}
        facts={<FactsPanel facts={pdp?.quickFacts} />}
        compare={<ComparePanel rows={pdp?.comparison} productName={product.title} />}
        bundleOffer={!comingSoon && hasFbtOffer(product.handle) && variant?.id ? (
          <FrequentlyBoughtTogether
            handle={product.handle}
            qty={qty}
            currentImage={product.images.edges[0]?.node.url ?? null}
            currentTitle={product.title}
            currentVariantId={variant.id}
            onAdded={() => (window as unknown as { __openCart?: () => void }).__openCart?.()}
          />
        ) : undefined}
        formula={product.handle === "liver-biome-plus" ? <LiverResearchCards /> : null}
        evidence={<>
          <a className="twc-text-link" href="/ingredients">Explore ingredient research</a>
          <OvaVitaminD3Evidence handle={product.handle} />
          {productIdForHandle(product.handle) && <ReviewsSection productId={productIdForHandle(product.handle)!} />}
        </>}
        routine={<>
          <p>Follow the directions on your product label. Make your routine consistent, and discuss suitability with your clinician when needed.</p>
          <a className="twc-text-link" href="/contact">Ask about product directions</a>
        </>}
        recap={<>
          {comingSoon ? <p>{product.handle === "ova-balance" ? "Pre-booking is open. Reserve Ova Balance for free. No payment is collected now." : "This formula is coming soon. Pre-reserve to register your interest."}</p> : <p>{label}{effective ? ` · ${effective.unitsLabel} · ${formatInr(effective.price)}` : ""}</p>}
          {!comingSoon && effective?.isCampaign && <p>{effective.subLabel}</p>}
          {!comingSoon && PREORDER_ACTIVE && <p>{PREORDER_NOTE_SHORT}</p>}
        </>}
        faqs={(pdp?.faqs ?? []).filter(f => !/when should|how.*take|how long|how much|dosage|serving|bio.absorb/i.test(f.q))}
      />
      <PreBookUpsellSheet open={upsellOpen} onOpenChange={setUpsellOpen} handle={product.handle} qty={qty}
        currentImage={product.images.edges[0]?.node.url ?? null} currentTitle={product.title} currentVariantId={variant?.id ?? ""}
        isLoading={isLoading} onAdded={() => { setUpsellOpen(false); (window as unknown as { __openCart?: () => void }).__openCart?.(); }}
        onSkip={() => { setUpsellOpen(false); void handleAdd(false); }} />
      <OvaProtocolLinks handle={product.handle} />

    </>;
  }

  return (
    <div
      className="mx-auto max-w-7xl px-4 py-6 md:px-8 md:py-12"
      style={
        {
          "--brand": theme.brand,
          "--brand-deep": theme.brandDeep,
          "--brand-soft": theme.brandSoft,
          "--primary": theme.brand,
          "--ring": theme.brand,
          "--accent": theme.brandSoft,
          "--accent-foreground": theme.brandDeep,
        } as React.CSSProperties
      }
    >
      <div className="grid items-start gap-8 md:grid-cols-2 md:gap-12">
        <div className="min-w-0 md:self-start">
          {/* Mobile: swipeable gallery + thumbnail strip */}
          <div className="-mx-4 md:hidden">
            <div className="relative">
              <SwipeableImage
                images={galleryImagesResolved}
                active={activeImg}
                onPrev={() => setActiveImg((i) => (i - 1 + galleryImagesResolved.length) % galleryImagesResolved.length)}
                onNext={() => setActiveImg((i) => (i + 1) % galleryImagesResolved.length)}
              />
              <GalleryActions handle={product.handle} title={product.title} />
            </div>
            {galleryImagesResolved.length > 1 && (
              <div className="mt-3 flex items-center justify-center gap-1.5">
                {galleryImagesResolved.map((_: { src: string; alt: string }, i: number) => (
                  <button
                    key={`dot-${i}`}
                    type="button"
                    aria-label={`Show image ${i + 1}`}
                    onClick={() => setActiveImg(i)}
                    className={`h-1.5 rounded-full transition-all ${
                      i === activeImg
                        ? "w-5 bg-[#2962FF]"
                        : "w-1.5 bg-[#2962FF]/25"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
          {/* Desktop: long vertically-scrolling image stack */}
          <div className="hidden md:flex md:flex-col md:gap-4">
            {galleryImagesResolved.map((img: { src: string; alt: string }, i: number) => (
              <div
                key={`stack-${i}`}
                className={`relative overflow-hidden rounded-2xl border border-border bg-[oklch(0.985_0.003_240)] ${i === 0 ? "img-premium" : ""}`}
              >
                {i === 0 && <GalleryActions handle={product.handle} title={product.title} />}
                <img
                  src={img.src}
                  alt={img.alt}
                  loading={i === 0 ? "eager" : "lazy"}
                  {...(i === 0 ? { fetchPriority: "high" as const, decoding: "async" as const } : { decoding: "async" as const })}
                  className="aspect-square w-full object-contain p-6"
                />
              </div>
            ))}
          </div>
        </div>
        <div className="min-w-0 md:sticky md:top-24">

          {/* Bio-Absorb hero stat, number → proof → plan */}
          <a
            href="#bio-absorb-data"
            className="group flex w-full items-stretch overflow-hidden rounded-2xl border border-brand/15 bg-brand-soft/40 transition hover:border-brand/40 hover:shadow-[0_8px_30px_-12px_color-mix(in_oklab,var(--brand)_40%,transparent)]"
          >
            <div className="flex items-center justify-center bg-brand px-4 py-3 md:px-5">
              <span className="font-display text-2xl font-bold leading-none text-white tabular-nums md:text-3xl">
                +68<span className="text-base align-top md:text-lg">%</span>
              </span>
            </div>
            <div className="px-4 py-2.5 md:px-5">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand">
                Avg absorption uplift
              </p>
              <p className="mt-0.5 text-[12px] leading-snug text-brand-deep/85 md:text-[13px]">
                Liposomal carriers vs standard forms, across 5 peer-reviewed research.{" "}
                <span className="font-semibold text-brand underline-offset-2 group-hover:underline">
                  See the data ↓
                </span>
              </p>
            </div>
          </a>

          {/* Big uppercase title */}
          <h1 className="mt-4 font-display text-3xl font-bold uppercase leading-[1.05] tracking-tight text-brand-deep md:text-5xl">
            {product.title}
          </h1>
          <p className="mt-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-brand md:text-[13px]">
            Liposomal forms / Patented Ingredients + Bio-Absorb <em>Matrix</em> for advanced absorption support
          </p>
          <UrgencyLabel handle={product.handle} className="mt-3" />
          <UrgencyInline placement="pdp" className="mt-3" />

          <div className="mt-3">
            <ReadWithLlm
              title={product.title}
              url={`https://truwe.in/products/${product.handle}`}
              kind="product"
            />
          </div>


          {/* Mag-only: positioning line + hero stats strip */}
          {product.handle === "mag-absorb-pro" && (
            <>
              <p className="mt-3 text-[12px] font-bold uppercase tracking-[0.16em] text-brand md:text-[13px]">
                &nbsp;5-FORM MAGNESIUM COMPLEX WITH BIO-ABSORB <em>Matrix</em>
              </p>
              <div className="mt-4 flex flex-col gap-2 rounded-2xl border border-border bg-card px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4">
                {[
                  "5 Bioavailable Forms",
                  "Advanced Delivery",
                ].map((s) => (
                  <span
                    key={s}
                    className="inline-flex min-w-0 items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-deep md:text-[12px]"
                  >
                    <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                    <span className="min-w-0">{s}</span>
                  </span>
                ))}
              </div>

            </>
          )}

          {/* Tagline, first <strong> in description */}
          {(() => {
            const html = (product as unknown as { descriptionHtml?: string }).descriptionHtml ?? "";
            const m = html.match(/<strong>([^<]+)<\/strong>/);
            const englishTagline = m?.[1] ?? product.description.split(/[.\n]/)[0];
            const tagline = localizedTagline(product.handle, englishTagline);
            return tagline ? (
              <p className="mt-4 text-base leading-relaxed text-foreground/80 md:text-lg">
                {tagline}
              </p>
            ) : null;
          })()}

          <div className="mt-6 h-px w-full bg-border" />

          {/* Quick benefits chip grid */}
          {(() => {
            const benefits = getQuickBenefitsForHandle(product.handle);
            if (!benefits) return null;
            return (
              <div className="mt-6 grid grid-cols-3 gap-2 md:gap-4">
                {benefits.map((b, i) => (
                  <div
                    key={b.label}
                    className={`min-w-0 flex-col items-center gap-2 rounded-xl border border-border bg-card px-2 py-3 text-center sm:flex-row sm:items-center sm:gap-2.5 sm:text-left md:gap-3 md:px-3 ${
                      i > 2 ? "hidden md:flex" : "flex"
                    }`}
                  >
                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-soft md:h-10 md:w-10">
                      <b.icon className="h-4 w-4 text-brand md:h-[18px] md:w-[18px]" strokeWidth={1.75} />
                    </span>
                    <span className="min-w-0 break-words hyphens-auto whitespace-pre-line text-[11px] font-semibold leading-tight text-brand-deep md:text-xs">
                      {b.label}
                    </span>
                  </div>
                ))}
              </div>

            );
          })()}

          {/* Price block — campaign-aware shared resolver */}
          {(() => {
            const eff = getEffectivePack(product.handle, qty, activeCampaign, pageOverride);
            const displayPrice = eff ? eff.price : price * qty;
            const displayCompare = eff ? eff.compareAt : (compare ?? 0) * qty;
            const showStrike = displayCompare > displayPrice;
            const saved = eff ? eff.save : Math.max(0, displayCompare - displayPrice);
            return (
              <div className="mt-8 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="font-display text-3xl font-bold text-brand-deep md:text-4xl">
                  {formatInr(displayPrice)}
                </span>
                {showStrike && (
                  <span className="text-base text-muted-foreground/80 line-through md:text-lg">
                    {formatInr(displayCompare)}
                  </span>
                )}
                <span className="text-[11px] text-muted-foreground">
                  {saved > 0 ? `Save ${formatInr(saved)} · ` : ""}
                  {eff?.compareBasis === "singles"
                    ? `vs ${qty} single bottles · incl. all taxes`
                    : "MRP incl. all taxes"}
                </span>
              </div>
            );
          })()}

          {/* Razorpay Affordability Widget. EMI / Pay Later / cardless offers.
              Based on the amount actually payable for the current selection. */}
          {(() => {
            const eff = getEffectivePack(product.handle, qty, activeCampaign, pageOverride);
            const displayPrice = eff ? eff.price : price * qty;
            return (
              <RazorpayAffordabilityWidget
                amountPaise={Math.round(displayPrice * 100)}
              />
            );
          })()}

          {/* Pack-size selector (quick-buy sheet dismissal lands here).
              While a campaign is live the qualifying pack IS the offer — there
              is never a second selectable card for the same bottles. */}
          <div id="buy-box" className="scroll-mt-24" data-engagement-section="buy-box">
            <PackSizeSelector
              handle={product.handle}
              price={price}
              qty={qty}
              onSelect={setQty}
              override={pageOverride}
              campaign={activeCampaign}
            />
          </div>

          {/* Inaugural offer artwork (selects the protocol pack) */}
          {activeCampaign &&
            (hasInauguralArt(product.handle) ? (
              <InauguralOfferBanner
                className="mt-4"
                handle={product.handle}
                selected={qty === activeCampaign.qualifyingQty}
                onSelect={() => setQty(activeCampaign.qualifyingQty)}
              />
            ) : (
              <InauguralProtocolCard
                className="mt-4"
                campaign={activeCampaign}
                unitPrice={
                  getEffectivePack(product.handle, 1, null, pageOverride)?.price ?? price
                }
                selected={qty === activeCampaign.qualifyingQty}
              />
            ))}


          {/* Bio-Absorb Matrix explainer */}
          <BioAbsorbExplainer />

          {/* Payment + trust messaging near the buy CTA */}
          <PaymentTrustBadges variant="default" />

          {/* Delivery + COD/EMI trust bar */}
          <PincodeDeliveryBar />

          {/* Tiered-pack products: trust signals near ATC */}

          {isFallback && (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12px] leading-snug text-amber-900">
              <strong className="font-semibold">Live pricing & checkout temporarily unavailable.</strong>{" "}
              We're showing reference details while our store reconnects. Please check back shortly.
            </div>
          )}

          {/* <FoundingMemberForm productHandle={product.handle} productName={product.title} /> */}

          {/* Ova Balance: scarcity callout above the pre-reserve CTA */}
          {product.handle === "ova-balance" && (
            <div className="mt-4 flex items-center gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3.5 py-2.5">
              <span aria-hidden className="relative flex h-2 w-2 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-pulse rounded-full bg-amber-500 opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
              </span>
              <p className="text-[12px] font-semibold uppercase tracking-[0.08em] leading-snug text-amber-700">
                Limited quantities — booking fast
                <span className="block text-[11px] font-medium normal-case tracking-normal text-amber-700/90">
                  Only a small batch is available in this pre-book window. Reserve yours early.
                </span>
              </p>
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 gap-3">
            {SOLD_OUT || isComingSoon(product.handle) ? (
              <>
                <PreReserveDialog
                  productHandle={product.handle}
                  productName={product.title}
                  source="pdp-sold-out"
                  trigger={
                    <Button
                      size="lg"
                      className="h-12 rounded-full bg-foreground text-sm font-semibold text-background transition-transform hover:bg-foreground/90 active:scale-[0.98]"
                    >
                      {product.handle === "ova-balance" ? "Pre-book now" : "Pre-Reserve"}
                    </Button>
                  }
                />
                <NotifyMeDialog
                  productName={product.title}
                  source="pdp-sold-out"
                  trigger={
                    <Button
                      variant="outline"
                      size="lg"
                      className="h-12 rounded-full border-foreground/15 text-sm font-semibold text-foreground"
                    >
                      Notify me
                    </Button>
                  }
                />
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="lg"
                  className="h-12 rounded-full border-foreground/15 text-sm font-semibold text-foreground"
                  disabled={isFallback || !variant?.availableForSale || isLoading}
                  onClick={() => handleAdd(false)}
                  data-ci-click="pdp-add-to-cart"
                >
                  {isLoading ? "Adding…" : justAdded ? "Added ✓" : "Add to Cart"}
                </Button>
                <Button
                  size="lg"
                  className="h-12 rounded-full bg-foreground text-sm font-semibold text-background transition-transform hover:bg-foreground/90 active:scale-[0.98]"
                  disabled={isFallback || !variant?.availableForSale || isLoading}
                  onClick={() => {
                    if (PREORDER_ACTIVE && hasFbtOffer(product.handle) && variant?.id) {
                      setUpsellOpen(true);
                      return;
                    }
                    void handleAdd(true);
                  }}
                  data-ci-click="pdp-buy-now"
                >

                  {PREORDER_ACTIVE ? "Pre-book now" : "Buy Now"}
                </Button>
              </>
            )}
          </div>

          {PREORDER_ACTIVE && !SOLD_OUT && !isComingSoon(product.handle) && (
            <p className="mt-2.5 rounded-xl border border-brand/20 bg-brand-soft/40 px-3 py-2 text-[12px] font-medium leading-snug text-brand-deep">
              {PREORDER_NOTE}. Full payment secures your batch; we dispatch every pre-book order on{" "}
              {PREORDER_DISPATCH_LABEL}.
            </p>
          )}

          {/* Optional add-on, deliberately AFTER the primary action so the
              visitor finishes one decision before seeing a second one. */}
          <FrequentlyBoughtTogether
            handle={product.handle}
            qty={qty}
            currentImage={product.images.edges[0]?.node.url ?? null}
            currentTitle={product.title}
            currentVariantId={variant?.id ?? ""}
          />

          {/* Auto-applied offers, compact */}
          {(() => {
            const eff = getEffectivePack(product.handle, qty, activeCampaign, pageOverride);
            const subtotal = eff ? eff.price : price * qty;
            const prepaidDiscount = prepaid ? 1 : 0;
            const afterPrepaid = prepaid ? prepaidEstimate(subtotal) : subtotal;
            const totalSavings = subtotal - afterPrepaid;
            const protocolLabel = eff?.isCampaign
              ? `${eff.campaignName ?? "Inaugural offer"} · ${qty * 30}-day protocol`
              : `${qty * 30}-day protocol`;
            return (
              <div className="mt-5 space-y-3">

                <div className="grid grid-cols-2 gap-2 rounded-xl border border-border p-1">
                  <button
                    onClick={() => setPrepaid(true)}
                    className={`rounded-lg px-3 py-2 text-left text-xs transition ${
                      prepaid ? "bg-foreground text-background" : "text-foreground hover:bg-muted"
                    }`}
                  >
                    <p className="font-semibold">Prepaid</p>
                    <p className={`text-[10px] ${prepaid ? "text-background/80" : "text-muted-foreground"}`}>
                      +5% off
                    </p>
                  </button>
                  <button
                    onClick={() => setPrepaid(false)}
                    className={`rounded-lg px-3 py-2 text-left text-xs transition ${
                      !prepaid ? "bg-foreground text-background" : "text-foreground hover:bg-muted"
                    }`}
                  >
                    <p className="font-semibold">Cash on delivery</p>
                    <p className={`text-[10px] ${!prepaid ? "text-background/80" : "text-muted-foreground"}`}>
                      Standard
                    </p>
                  </button>
                </div>

                {prepaidDiscount > 0 && (
                  <div className="rounded-xl bg-brand-deep px-3.5 py-2.5 text-white">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-white/75">
                          Estimated eligible-prepaid price · {protocolLabel}
                        </p>
                        <p className="font-display text-lg font-bold">
                          {formatInr(afterPrepaid)}
                        </p>
                      </div>
                      <p className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-bold">
                        Est. −{formatInr(totalSavings)}
                      </p>
                    </div>
                    <p className="mt-1.5 text-[10px] leading-snug text-white/70">
                      Cart total is {formatInr(subtotal)}. The extra 5% is applied once,
                      by the payment gateway on eligible prepaid methods — not in the cart,
                      and not on cash on delivery.
                    </p>
                  </div>
                )}
              </div>
            );
          })()}


          {/* Trust strip */}
          <div className="mt-6 grid grid-cols-3 gap-3 border-t border-border pt-5 text-[11px] text-muted-foreground">
            <div className="flex items-center justify-center gap-1.5">
              <FlaskConical className="h-3.5 w-3.5 text-brand" /> Science backed
            </div>
            <div className="flex items-center justify-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-brand" /> Lab tested
            </div>
            <div className="flex items-center justify-center gap-1.5">
              <Leaf className="h-3.5 w-3.5 text-brand" /> Vegan
            </div>
          </div>

          {/* Accordions */}
          <Accordion type="multiple" defaultValue={["description"]} className="mt-6 border-t border-border">
            <AccordionItem value="description" className="border-b border-border">
              <AccordionTrigger className="py-4 text-base font-semibold uppercase tracking-wide text-brand-deep hover:no-underline">
                {tt("pdp.description")}
              </AccordionTrigger>
              <AccordionContent>
                <ExpandableHtml
                  html={
                    localizedDescription(product.handle, product.description) !== product.description
                      ? `<p>${localizedDescription(product.handle, product.description)}</p>`
                      : (product as unknown as { descriptionHtml?: string }).descriptionHtml ??
                        `<p>${product.description}</p>`
                  }
                />
                {localizedRoutine(product.handle) && (
                  <div className="mt-4 border-t border-border pt-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-brand-deep">
                      {tt("pdp.howToUse")}
                    </p>
                    <p className="mt-1.5 text-sm text-muted-foreground">
                      {localizedRoutine(product.handle)}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">{tt("pdp.routineNote")}</p>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="why-choose" className="border-b border-border">
              <AccordionTrigger className="py-4 text-base font-semibold uppercase tracking-wide text-brand-deep hover:no-underline">
                Why Choose Us?
              </AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-3 pb-2 text-sm leading-relaxed text-foreground/80">
                  <li className="flex gap-2.5"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand" /><span><strong className="text-brand-deep">Research-backed doses</strong> every active matches the level used in peer-reviewed trials, not under-dosed for cost.</span></li>
                  <li className="flex gap-2.5"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand" /><span><strong className="text-brand-deep">Bioavailable forms</strong> liposomal carriers and chelated minerals chosen for published bioavailability data.</span></li>
                  <li className="flex gap-2.5"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand" /><span><strong className="text-brand-deep">Third-party lab tested</strong> every batch verified for purity, potency and heavy metals. <a href="/verify" className="text-brand underline underline-offset-2 hover:text-brand-deep">Verify your batch →</a></span></li>
                  <li className="flex gap-2.5"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand" /><span><strong className="text-brand-deep">Synergistic stacks</strong> formulated as systems, not single-ingredient pills.</span></li>
                  <li className="flex gap-2.5"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand" /><span><strong className="text-brand-deep">Clean label</strong> vegan, no synthetic fillers, no artificial colours.</span></li>
                </ul>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="how-it-works" className="border-b border-border">
              <AccordionTrigger className="py-4 text-base font-semibold uppercase tracking-wide text-brand-deep hover:no-underline">
                How Does It Work?
              </AccordionTrigger>
              <AccordionContent>
                <ol className="space-y-3 pb-2 text-sm leading-relaxed text-foreground/80">
                  <li className="flex gap-3"><span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand text-[11px] font-bold text-white">1</span><span><strong className="text-brand-deep">Take daily, with food.</strong> Consistency is what drives results, most peer-reviewed research measure outcomes over 8-12 weeks.</span></li>
                  <li className="flex gap-3"><span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand text-[11px] font-bold text-white">2</span><span><strong className="text-brand-deep">Supports absorption.</strong> Liposomal carriers protect actives through stomach acid to support bioavailability versus standard powder forms.</span></li>
                  <li className="flex gap-3"><span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand text-[11px] font-bold text-white">3</span><span><strong className="text-brand-deep">Acts at the source.</strong> Each active targets a specific pathway sleep, hormones, detox to restore balance, not just mask symptoms.</span></li>
                  <li className="flex gap-3"><span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand text-[11px] font-bold text-white">4</span><span><strong className="text-brand-deep">You feel the shift.</strong> Most users notice changes within 2-4 weeks; full benefits compound by week 8.</span></li>
                </ol>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {(() => {
        const pdp = mergePdpForHandle(product.handle, pageOverride);
        const pid = productIdForHandle(product.handle);
        if (!pdp) return null;
        return (
          <>
            <Reveal><TLDRCard tldr={pdp.tldr} /></Reveal>
            {product.handle === "ova-balance" && <OvaCommunityIllustration />}

            {product.handle === "liver-biome-plus" ? (
              <Reveal>
                <div data-engagement-section="ingredients">
                  <LiverResearchCards />
                </div>
              </Reveal>
            ) : null}
            <Reveal><StudiedEffects handle={product.handle} /></Reveal>
            {pid && (
              <Reveal>
                <div data-engagement-section="reviews">
                  <ReviewsSection productId={pid} />
                </div>
              </Reveal>
            )}
            <Reveal><ProductAbsorptionChart handle={product.handle} /></Reveal>
            <Reveal><QuickFactsTable facts={pdp.quickFacts} /></Reveal>
            <Reveal>
              <div data-engagement-section="comparison">
                <ComparisonTable rows={pdp.comparison} productName={product.title} />
              </div>
            </Reveal>
          </>
        );
      })()}

      <Reveal><ProtocolBundle currentHandle={product.handle} /></Reveal>
      <Reveal><OvaProtocolLinks handle={product.handle} /></Reveal>

      <Reveal><WhySupplementsFailSection /></Reveal>

      {product.handle === "liver-biome-plus" && (
        <Reveal><GutLiverAxisSection /></Reveal>
      )}

      {(() => {
        const pdp = mergePdpForHandle(product.handle, pageOverride);
        if (!pdp) return null;
        return (
          <Reveal>
            <div data-engagement-section="faq">
              <PDPFaq faqs={pdp.faqs} />
            </div>
          </Reveal>
        );
      })()}

      {(() => {
        const related = articlesForProduct(product.handle);
        if (!related.length) return null;
        return (
          <Reveal>
            <div className="mx-auto max-w-6xl px-4 md:px-8">
              <RelatedReading
                articles={related}
                heading="From the TruWe library"
                eyebrow="Understand the science"
              />
            </div>
          </Reveal>
        );
      })()}

      {(() => {
        const slugs = COLLECTIONS_FOR_PRODUCT[product.handle] ?? [];
        if (!slugs.length) return null;
        return (
          <Reveal>
            <section aria-labelledby="pdp-collections" className="mx-auto mt-10 max-w-6xl px-4 md:px-8">
              <h2 id="pdp-collections" className="font-display text-2xl font-bold text-brand-deep md:text-3xl">
                Browse by need
              </h2>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {slugs.map((s) => {
                  const c = COLLECTIONS[s];
                  return (
                    <li key={s}>
                      <TSLink
                        to="/collections/$slug"
                        params={{ slug: s }}
                        className="block rounded-2xl border border-border bg-background px-5 py-4 transition hover:border-brand"
                      >
                        <p className="text-sm font-semibold text-brand-deep">{c.breadcrumbLabel} <span className="text-brand">→</span></p>
                        <p className="mt-1 text-xs text-muted-foreground">{c.seoDescription}</p>
                      </TSLink>
                    </li>
                  );
                })}
              </ul>
            </section>
          </Reveal>
        );
      })()}


      <StickyMobileBuyBar
        title={product.title}
        handle={product.handle}
        price={price}
        compare={compare}
        qty={qty}
        currency={variant?.price.currencyCode ?? "INR"}
        isLoading={isLoading}
        justAdded={justAdded}
        disabled={SOLD_OUT || isComingSoon(product.handle) || isFallback || !variant?.availableForSale}
        soldOut={SOLD_OUT || isComingSoon(product.handle)}
        onAdd={() => {
          // Pre-book routes through the floating add-on step first, then cart.
          if (hasFbtOffer(product.handle) && variant?.id) {
            setUpsellOpen(true);
            return;
          }
          void handleAdd(false);
        }}
      />

      <PreBookUpsellSheet
        open={upsellOpen}
        onOpenChange={setUpsellOpen}
        handle={product.handle}
        qty={qty}
        currentImage={product.images.edges[0]?.node.url ?? null}
        currentTitle={product.title}
        currentVariantId={variant?.id ?? ""}
        isLoading={isLoading}
        onAdded={() => {
          setUpsellOpen(false);
          (window as unknown as { __openCart?: () => void }).__openCart?.();
        }}
        onSkip={() => {
          setUpsellOpen(false);
          void handleAdd(false);
        }}
      />
    </div>
  );
}

function StickyMobileBuyBar({
  title,
  handle,
  price,
  compare,
  qty,
  currency,
  isLoading,
  justAdded,
  disabled,
  soldOut = false,
  onAdd,
}: {
  title: string;
  handle: string;
  price: number;
  compare: number | null;
  qty: number;
  currency: string;
  isLoading: boolean;
  justAdded: boolean;
  disabled: boolean;
  soldOut?: boolean;
  onAdd: () => void;
}) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Let floating widgets (chat bubble) know a sticky CTA occupies the bottom.
  useEffect(() => {
    document.body.classList.toggle("has-sticky-cta", show);
    return () => document.body.classList.remove("has-sticky-cta");
  }, [show]);

  // Match the main selector exactly — one shared, campaign-aware resolver.
  const stickyCampaign = useInauguralCampaign(handle);
  const effective = getEffectivePack(handle, qty, stickyCampaign);
  const final = effective ? effective.price : price * qty;
  const mrp = effective ? effective.compareAt : (compare ?? 0) * qty;
  const saved = effective ? effective.save : Math.max(0, mrp - final);
  const supplyLabel = qty === 3 ? "90-day protocol" : qty === 2 ? "60-day protocol" : "30-day starter";
  const cartCount = useCartStore((s) => s.items.reduce((n, i) => n + i.quantity, 0));
  const openCart = () =>
    (window as unknown as { __openCart?: () => void }).__openCart?.();

  return (
    <div
      className={`fixed inset-x-0 z-50 mx-auto max-w-[430px] rounded-2xl border border-border bg-background/95 px-4 py-3 shadow-[0_10px_30px_-8px_rgba(43,45,66,0.25)] backdrop-blur-md transition-transform duration-300 md:hidden ${
        show ? "translate-y-0" : "translate-y-[140%]"
      }`}
      style={{ bottom: "calc(env(safe-area-inset-bottom) + 12px)", left: "0.75rem", right: "0.75rem" }}
    >

      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
        <div className="min-w-0">
          {cartCount > 0 && (
            <button
              type="button"
              onClick={openCart}
              className="mb-1 inline-flex items-center gap-1.5 rounded-full bg-brand/10 px-2.5 py-1 text-[11px] font-semibold text-brand-deep transition-transform active:scale-95"
            >
              <ShoppingCart className="h-3.5 w-3.5" strokeWidth={2} />
              View cart · {cartCount}
            </button>
          )}
          <p className="text-[10px] font-semibold uppercase leading-snug tracking-wider text-muted-foreground">
            {soldOut
              ? `${supplyLabel} · ${handle === "ova-balance" ? "Pre-booking open" : "Launching soon"}`
              : PREORDER_ACTIVE
                ? `${supplyLabel} · ${PREORDER_NOTE_SHORT}`
                : supplyLabel}
          </p>
          <p className="font-display text-base font-bold text-brand-deep tabular-nums">
            ₹{Math.round(final)}
            {saved > 0 && (
              <span className="ml-1.5 text-[11px] font-normal text-muted-foreground line-through">
                ₹{Math.round(mrp)}
              </span>
            )}
            {saved > 0 && (
              <span className="ml-1.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200">
                Save ₹{Math.round(saved)}
              </span>
            )}
          </p>
        </div>
        {soldOut ? (
          <PreReserveDialog
            productHandle={handle}
            productName={title}
            source="pdp-sticky-bar"
            trigger={
              <Button
                size="lg"
                className="h-11 flex-shrink-0 rounded-full bg-foreground px-6 text-sm font-semibold text-background transition-all hover:bg-foreground/90 active:scale-[0.97]"
              >
                {handle === "ova-balance" ? "Pre-book now" : "Pre-Reserve"}
              </Button>
            }
          />
        ) : (
          <Button
            size="lg"
            className={`h-11 flex-shrink-0 rounded-full px-6 text-sm font-semibold transition-all active:scale-[0.97] ${
              justAdded ? "bg-brand text-white" : "bg-foreground text-background hover:bg-foreground/90"
            }`}
            onClick={onAdd}
            disabled={isLoading || disabled}
            data-ci-click="pdp-sticky-add-to-cart"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : justAdded ? (
              <span className="inline-flex items-center gap-1.5 animate-fade-in">
                <Check className="h-4 w-4" /> Added
              </span>
            ) : (
              "Buy now"
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

function WhySupplementsFailSection() {
  const points = [
    {
      title: "Underdosed actives",
      body: "Most supplements use 10-50× lower doses than the levels used in research studies.",
    },
    {
      title: "Poor bioavailability",
      body: "Cheap forms barely absorb, ingredients pass through without reaching target tissues.",
    },
    {
      title: "No synergy",
      body: "Single-ingredient pills miss the combinations that actually move the needle together.",
    },
    {
      title: "No third-party testing",
      body: "Without independent lab verification, label claims and real contents rarely match.",
    },
  ];
  return (
    <section className="mt-10 md:mt-16">
      <div className="mx-auto max-w-3xl rounded-3xl border border-border bg-brand-soft/40 p-5 md:p-8">
        <h2 className="font-display text-3xl font-bold text-brand-deep md:text-4xl">
          Why most supplements fail
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground/75 md:text-base">
          Most supplements use “fairy dust” dosing, just enough to list on the label, but nowhere
          near the therapeutic levels needed for real results.
        </p>
        <ul className="mt-6 space-y-4">
          {points.map((p) => (
            <li key={p.title} className="flex items-start gap-3.5">
              <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-brand text-white shadow-sm">
                <Check className="h-4 w-4" strokeWidth={3} />
              </span>
              <div>
                <p className="font-display text-base font-semibold text-brand-deep">{p.title}</p>
                <p className="mt-0.5 text-sm leading-relaxed text-foreground/75">{p.body}</p>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-7 rounded-2xl border border-brand/20 bg-background/70 p-4 md:p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
            The TruWe difference
          </p>
          <p className="mt-2 text-sm leading-relaxed text-brand-deep md:text-base">
            Research-backed doses. Highly bioavailable forms. Synergistic stacks. Every batch
            third-party lab tested, so what's on the label is what reaches your cells.
          </p>
        </div>
      </div>
    </section>
  );
}

function ProductAbsorptionChart({ handle }: { handle: string }) {
  const key = getFormulaKeyForHandle(handle);
  if (!key) return null;
  const rows = PRODUCT_ABSORPTION_ROWS[key];
  const titles: Record<"liver" | "mag" | "ova", { eyebrow: string; title: string; description: string }> = {
    liver: {
      eyebrow: "Absorption Research · Liver Biome Plus",
      title: "Liposomal carriers + Bio-Absorb blend, published uptake data.",
      description:
        "Each active in Liver Biome Plus mapped against its commodity counterpart. Liposomal Curcumin, Silymarin and Alpha-Lipoic Acid plus acid-protected probiotics and NAC, paired with our Bio-Absorb Matrix blend (Black Pepper · Ginseng Extract · Quercetin · Ginger, exact proportions proprietary)."
    },
    mag: {
      eyebrow: "Absorption Research · Mag Absorb Pro",
      title: "5 magnesium forms vs Magnesium Oxide.",
      description:
        "Bioavailability reported in published studies for each of the 5 magnesium forms in Mag Absorb Pro Liposomal Bisglycinate, Aquamin® marine, Chloride, Aspartate and Gluconate compared with the Mg Oxide used in most retail magnesium products.",
    },
    ova: {
      eyebrow: "Absorption Research · Ova Balance",
      title: "Endocrine actives mapped to their best-absorbed forms.",
      description:
        "Each active in Ova Balance compared with its standard counterpart: gold-standard 40:1 Myo:D-Chiro Inositol, Berberine HCl with Bio-Absorb Matrix, Quatrefolic® L-5-MTHF folate, chelated Magnesium Bisglycinate and Chromium Picolinate.",
    },
  };
  const copy = titles[key];
  return (
    <div id="bio-absorb-data" className="mx-auto mt-10 max-w-5xl scroll-mt-24">
      <AbsorptionRateChart
        rows={rows}
        eyebrow={copy.eyebrow}
        title={copy.title}
        description={copy.description}
      />
      <OvaVitaminD3Evidence handle={handle} />
    </div>
  );
}


function ProductImageStrip({
  images,
  active,
  onSelect,
}: {
  images: { src: string; alt: string }[];
  active: number;
  onSelect: (i: number) => void;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  const scrollBy = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8 * dir;
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <div className="relative mt-3 md:mt-4">
      <div
        ref={scrollerRef}
        className="flex snap-x snap-mandatory gap-2.5 overflow-x-auto scroll-smooth px-0.5 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {images.map((img, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onSelect(i)}
            className={`relative shrink-0 snap-start overflow-hidden rounded-xl ring-2 ring-offset-2 ring-offset-background transition ${
              i === active ? "ring-brand" : "ring-transparent hover:ring-brand/40"
            }`}
            aria-label={`View image ${i + 1}`}
          >
            <img
              src={img.src}
              alt={img.alt}
              className="h-14 w-14 rounded-xl bg-brand-soft object-contain p-1 md:h-16 md:w-16"
              loading="lazy"
            />
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => scrollBy(-1)}
        className="absolute -left-3 top-1/2 hidden -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/90 p-2 shadow-sm backdrop-blur transition hover:bg-background md:flex"
        aria-label="Scroll images left"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => scrollBy(1)}
        className="absolute -right-3 top-1/2 hidden -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/90 p-2 shadow-sm backdrop-blur transition hover:bg-background md:flex"
        aria-label="Scroll images right"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function SwipeableImage({
  images,
  active,
  onPrev,
  onNext,
}: {
  images: { src: string; alt: string }[];
  active: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const startX = useRef<number | null>(null);
  const startY = useRef<number | null>(null);
  const locked = useRef<"h" | "v" | null>(null);
  const [drag, setDrag] = useState(0);
  const [dragging, setDragging] = useState(false);

  const onTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    locked.current = null;
    setDragging(true);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (startX.current == null || startY.current == null) return;
    const dx = e.touches[0].clientX - startX.current;
    const dy = e.touches[0].clientY - startY.current;
    if (!locked.current) {
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
        locked.current = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
      }
    }
    if (locked.current === "h") setDrag(dx);
  };
  const onTouchEnd = () => {
    const width = containerRef.current?.clientWidth ?? 1;
    const threshold = Math.min(80, width * 0.2);
    if (drag > threshold && images.length > 1) onPrev();
    else if (drag < -threshold && images.length > 1) onNext();
    startX.current = null;
    startY.current = null;
    locked.current = null;
    setDragging(false);
    setDrag(0);
  };

  if (!images.length) return null;
  const width = containerRef.current?.clientWidth ?? 0;
  const offsetPct = width ? (drag / width) * 100 : 0;

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden touch-pan-y select-none"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="flex will-change-transform"
        style={{
          transform: `translate3d(calc(${-active * 100}% + ${offsetPct}%), 0, 0)`,
          transition: dragging ? "none" : "transform 520ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        {images.map((img, i) => (
          <img
            key={i}
            src={img.src}
            alt={img.alt}
            draggable={false}
            loading={i === 0 ? "eager" : "lazy"}
            decoding="async"
            {...(i === 0 ? { fetchPriority: "high" as const } : {})}
            className="block h-auto w-full shrink-0 object-cover"
          />
        ))}
      </div>
    </div>
  );
}



// Canonical source lives in src/lib/checkout-pricing.ts (shared server + client).
import { PACK_PRICING as _PACK_PRICING } from "@/lib/checkout-pricing";
export const TIERED_PACK_PRICING: Record<string, Record<1 | 2 | 3, { price: number; mrp: number }>> = _PACK_PRICING;

// Back-compat alias
export const MAG_PACK_PRICING = TIERED_PACK_PRICING["mag-absorb-pro"];




